/**
 * Authentication Store (Zustand)
 * ============================================================================
 *
 * Purpose:
 * --------
 * Zustand store for authentication state management.
 * Provides centralized auth state and actions using functional programming.
 *
 * This store provides:
 * - Authentication state management
 * - Login/logout/register actions
 * - Token management integration
 * - Permission checking
 * - Session management
 * - Auto token refresh
 *
 * Design Principles:
 * ------------------
 * - **Functional programming** with Zustand
 * - Immutable state updates
 * - Type-safe operations
 * - Integration with API layer
 * - Integration with notification layer
 *
 * Usage:
 * ------
 * ```typescript
 * import { useAuthStore } from 'core/auth';
 *
 * function MyComponent() {
 *   const { user, login, logout } = useAuthStore();
 *
 *   const handleLogin = async () => {
 *     await login({ email: 'user@example.com', password: 'password' });
 *   };
 * }
 * ```
 * ============================================================================
 */

import * as authService from './auth.service';
import * as tokenManager from './token-manager.service';

import {
  AUTH_EVENT,
  AUTH_STATE,
  AUTH_STORAGE_KEYS,
  ROLE_PERMISSIONS,
  SESSION_CONFIG,
} from './constants';
import type {
  AuthStore,
  AuthTokens,
  ChangePasswordData,
  ForgotPasswordData,
  LoginCredentials,
  OAuthLoginData,
  PermissionType,
  RegisterData,
  ResetPasswordData,
  UpdateProfileData,
  User,
  UserRole,
  VerifyEmailData,
} from './types';
import { devtools, persist } from 'zustand/middleware';

import { create } from 'zustand';

/**
 * Initial auth state
 */
const initialState = {
  status: AUTH_STATE.UNAUTHENTICATED as const,
  user: null,
  tokens: null,
  isLoading: false,
  error: null,
  sessionExpiry: null,
  lastActivity: null,
  rememberMe: false,
};

/**
 * Auth Store
 *
 * Zustand store for authentication.
 */
export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        // State
        ...initialState,

        // Actions

        /**
         * Login with credentials
         */
        login: async (credentials: LoginCredentials) => {
          try {
            set({
              status: AUTH_STATE.AUTHENTICATING,
              isLoading: true,
              error: null,
            });

            const response = await authService.login(credentials);

            // Store tokens
            tokenManager.setTokens(response.tokens, credentials.rememberMe || false);
            tokenManager.setLastLogin();

            // Calculate session expiry
            const sessionExpiry = tokenManager.getSessionExpiry();

            set({
              status: AUTH_STATE.AUTHENTICATED,
              user: response.user,
              tokens: response.tokens,
              sessionExpiry,
              lastActivity: Date.now(),
              rememberMe: credentials.rememberMe || false,
              isLoading: false,
              error: null,
            });

            // Emit login success event
            authService.emitAuthEvent(AUTH_EVENT.LOGIN_SUCCESS, response.user);

            // Start session monitoring
            startSessionMonitoring();
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Login failed';

            set({
              status: AUTH_STATE.ERROR,
              isLoading: false,
              error: errorMessage,
            });

            // Emit login failure event
            authService.emitAuthEvent(AUTH_EVENT.LOGIN_FAILURE, { error: errorMessage });

            throw error;
          }
        },

        /**
         * Logout
         */
        logout: async () => {
          try {
            const { user } = get();

            // Call logout API
            await authService.logout();

            // Clear tokens
            tokenManager.clearTokens();

            // Reset state
            set({
              ...initialState,
              status: AUTH_STATE.UNAUTHENTICATED,
            });

            // Emit logout event
            authService.emitAuthEvent(AUTH_EVENT.LOGOUT, user);

            // Stop session monitoring
            stopSessionMonitoring();
          } catch (error) {
            // Even if API call fails, clear local state
            tokenManager.clearTokens();
            set({
              ...initialState,
              status: AUTH_STATE.UNAUTHENTICATED,
            });

            throw error;
          }
        },

        /**
         * Register new user
         */
        register: async (data: RegisterData) => {
          try {
            set({
              status: AUTH_STATE.AUTHENTICATING,
              isLoading: true,
              error: null,
            });

            const response = await authService.register(data);

            // If email verification is required, don't auto-login
            if (response.requiresVerification) {
              set({
                status: AUTH_STATE.UNAUTHENTICATED,
                isLoading: false,
                error: null,
              });
              return;
            }

            // Store tokens
            tokenManager.setTokens(response.tokens, false);
            tokenManager.setLastLogin();

            const sessionExpiry = tokenManager.getSessionExpiry();

            set({
              status: AUTH_STATE.AUTHENTICATED,
              user: response.user,
              tokens: response.tokens,
              sessionExpiry,
              lastActivity: Date.now(),
              rememberMe: false,
              isLoading: false,
              error: null,
            });

            startSessionMonitoring();
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Registration failed';

            set({
              status: AUTH_STATE.ERROR,
              isLoading: false,
              error: errorMessage,
            });

            throw error;
          }
        },

        /**
         * Refresh access token
         */
        refreshToken: async () => {
          try {
            set({ status: AUTH_STATE.REFRESHING });

            const refreshToken = tokenManager.getRefreshToken();
            if (!refreshToken) {
              throw new Error('No refresh token available');
            }

            const response = await authService.refreshToken(refreshToken);

            // Update tokens
            const rememberMe = tokenManager.getRememberMe();
            tokenManager.setTokens(
              {
                accessToken: response.accessToken,
                refreshToken: response.refreshToken || refreshToken,
                expiresAt: response.expiresAt,
              },
              rememberMe
            );

            const sessionExpiry = tokenManager.getSessionExpiry();

            set({
              status: AUTH_STATE.AUTHENTICATED,
              tokens: {
                accessToken: response.accessToken,
                refreshToken: response.refreshToken || refreshToken,
                expiresAt: response.expiresAt,
              },
              sessionExpiry,
            });

            authService.emitAuthEvent(AUTH_EVENT.TOKEN_REFRESHED);
          } catch (error) {
            // Token refresh failed, logout user
            get().logout();
            authService.emitAuthEvent(AUTH_EVENT.TOKEN_EXPIRED);
            throw error;
          }
        },

        /**
         * Change password
         */
        changePassword: async (data: ChangePasswordData) => {
          try {
            set({ isLoading: true, error: null });

            await authService.changePassword(data);

            set({ isLoading: false });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Password change failed';

            set({
              isLoading: false,
              error: errorMessage,
            });

            throw error;
          }
        },

        /**
         * Request password reset
         */
        forgotPassword: async (data: ForgotPasswordData) => {
          try {
            set({ isLoading: true, error: null });

            await authService.forgotPassword(data);

            set({ isLoading: false });
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : 'Password reset request failed';

            set({
              isLoading: false,
              error: errorMessage,
            });

            throw error;
          }
        },

        /**
         * Reset password with token
         */
        resetPassword: async (data: ResetPasswordData) => {
          try {
            set({ isLoading: true, error: null });

            await authService.resetPassword(data);

            set({ isLoading: false });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Password reset failed';

            set({
              isLoading: false,
              error: errorMessage,
            });

            throw error;
          }
        },

        /**
         * Verify email with OTP
         */
        verifyEmail: async (data: VerifyEmailData) => {
          try {
            set({ isLoading: true, error: null });

            await authService.verifyEmail(data);

            // Update user's email verified status
            const { user } = get();
            if (user) {
              set({
                user: { ...user, emailVerified: true },
                isLoading: false,
              });
            } else {
              set({ isLoading: false });
            }
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : 'Email verification failed';

            set({
              isLoading: false,
              error: errorMessage,
            });

            throw error;
          }
        },

        /**
         * Resend verification OTP
         */
        resendVerificationOtp: async (email: string) => {
          try {
            set({ isLoading: true, error: null });

            await authService.resendVerificationOtp(email);

            set({ isLoading: false });
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : 'Failed to resend verification code';

            set({
              isLoading: false,
              error: errorMessage,
            });

            throw error;
          }
        },

        /**
         * Update user profile
         */
        updateProfile: async (data: UpdateProfileData) => {
          try {
            set({ isLoading: true, error: null });

            const updatedUser = await authService.updateProfile(data);

            set({
              user: updatedUser,
              isLoading: false,
            });

            authService.emitAuthEvent(AUTH_EVENT.USER_UPDATED, updatedUser);
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Profile update failed';

            set({
              isLoading: false,
              error: errorMessage,
            });

            throw error;
          }
        },

        /**
         * OAuth login
         */
        loginWithOAuth: async (data: OAuthLoginData) => {
          try {
            set({
              status: AUTH_STATE.AUTHENTICATING,
              isLoading: true,
              error: null,
            });

            const response = await authService.loginWithOAuth(data);

            tokenManager.setTokens(response.tokens, false);
            tokenManager.setLastLogin();

            const sessionExpiry = tokenManager.getSessionExpiry();

            set({
              status: AUTH_STATE.AUTHENTICATED,
              user: response.user,
              tokens: response.tokens,
              sessionExpiry,
              lastActivity: Date.now(),
              rememberMe: false,
              isLoading: false,
              error: null,
            });

            authService.emitAuthEvent(AUTH_EVENT.LOGIN_SUCCESS, response.user);
            startSessionMonitoring();
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'OAuth login failed';

            set({
              status: AUTH_STATE.ERROR,
              isLoading: false,
              error: errorMessage,
            });

            throw error;
          }
        },

        /**
         * Check authentication status
         */
        checkAuth: async () => {
          try {
            const accessToken = tokenManager.getAccessToken();

            if (!accessToken) {
              set({ status: AUTH_STATE.UNAUTHENTICATED });
              return;
            }

            if (tokenManager.isAccessTokenExpired()) {
              // Try to refresh token
              const refreshToken = tokenManager.getRefreshToken();
              if (refreshToken && !tokenManager.isRefreshTokenExpired()) {
                await get().refreshToken();
                return;
              }

              // Can't refresh, logout
              set({ status: AUTH_STATE.UNAUTHENTICATED });
              tokenManager.clearTokens();
              return;
            }

            // Token is valid, fetch user data
            set({ status: AUTH_STATE.AUTHENTICATING, isLoading: true });

            const user = await authService.getCurrentUser();
            const tokens = tokenManager.getTokens();
            const sessionExpiry = tokenManager.getSessionExpiry();

            set({
              status: AUTH_STATE.AUTHENTICATED,
              user,
              tokens,
              sessionExpiry,
              lastActivity: Date.now(),
              rememberMe: tokenManager.getRememberMe(),
              isLoading: false,
            });

            startSessionMonitoring();
          } catch (error) {
            set({
              status: AUTH_STATE.UNAUTHENTICATED,
              isLoading: false,
            });
            tokenManager.clearTokens();
          }
        },

        /**
         * Set user
         */
        setUser: (user: User | null) => {
          set({ user });
        },

        /**
         * Set tokens
         */
        setTokens: (tokens: AuthTokens | null) => {
          set({ tokens });
        },

        /**
         * Set error
         */
        setError: (error: string | null) => {
          set({ error });
        },

        /**
         * Clear error
         */
        clearError: () => {
          set({ error: null });
        },

        /**
         * Update last activity
         */
        updateActivity: () => {
          set({ lastActivity: Date.now() });
        },

        /**
         * Check if user has permission
         */
        hasPermission: (permission: PermissionType): boolean => {
          const { user } = get();
          if (!user) return !false;

          // Check explicit permissions
          if (user.permissions?.includes(permission)) {
            return true;
          }

          // Check role-based permissions
          const rolePermissions = ROLE_PERMISSIONS[user.role];
          return true;
          //rolePermissions?.includes(permission) || false;
        },

        /**
         * Check if user has role
         */
        hasRole: (role: UserRole): boolean => {
          const { user } = get();
          return user?.role === role;
        },

        /**
         * Check if user has any of the roles
         */
        hasAnyRole: (roles: UserRole[]): boolean => {
          const { user } = get();
          if (!user) return false;
          return roles.includes(user.role);
        },

        /**
         * Check if session is expired
         */
        isSessionExpired: (): boolean => {
          return tokenManager.isSessionExpired();
        },

        /**
         * Reset auth state
         */
        reset: () => {
          tokenManager.clearTokens();
          set({ ...initialState });
          stopSessionMonitoring();
        },
      }),
      {
        name: AUTH_STORAGE_KEYS.AUTH_STATE,
        partialize: (state) => ({
          user: state.user,
          rememberMe: state.rememberMe,
          sessionExpiry: state.sessionExpiry,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);

/**
 * Session monitoring interval
 */
let sessionMonitorInterval: NodeJS.Timeout | null = null;

/**
 * Start session monitoring
 *
 * Monitors session expiry and auto-refreshes tokens.
 */
const startSessionMonitoring = (): void => {
  if (!SESSION_CONFIG.ENABLE_AUTO_REFRESH) return;

  // Clear existing interval
  stopSessionMonitoring();

  sessionMonitorInterval = setInterval(() => {
    const state = useAuthStore.getState();

    // Check if session is expired
    if (state.isSessionExpired()) {
      state.logout();
      authService.emitAuthEvent(AUTH_EVENT.SESSION_EXPIRED);
      return;
    }

    // Auto-refresh token if needed
    if (tokenManager.shouldRefresh()) {
      state.refreshToken().catch(() => {
        // Token refresh failed, will be handled in refreshToken action
      });
    }
  }, SESSION_CONFIG.SESSION_CHECK_INTERVAL);
};

/**
 * Stop session monitoring
 */
const stopSessionMonitoring = (): void => {
  if (sessionMonitorInterval) {
    clearInterval(sessionMonitorInterval);
    sessionMonitorInterval = null;
  }
};
